import { Fragment, MouseEvent, useMemo, useState } from 'react';
import { Box, Button, Modal, useMediaQuery } from '@mui/material';
import { Media } from '../../../../generated/graphql';
import _ from 'lodash';
import ImageGallery from 'react-image-gallery';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import {
  GalleryNavigationLeft,
  GalleryNavigationRight,
  ReactImageGalleryItemCustom,
  RenderGalleryItem,
  RenderGalleryThumbnail,
} from './PostMediaControls';
import 'react-image-gallery/styles/css/image-gallery.css';

export const PostMedia = ({
  media,
  isPostPage,
}: {
  media: Media[];
  isPostPage: boolean;
}) => {
  const [isGalleryOpen, setGalleryOpen] = useState<boolean>(false);
  const [startIndex, setStartIndex] = useState<number>(0);
  const isMobileView = useMediaQuery('(max-width:600px)');

  const mappedMedia = useMemo(
    () =>
      media.map((item) => ({
        original: `${item.cdn01}${
          item.mimeType?.startsWith('video') ? item.video : item.image
        }`,
        thumbnail: `${item.cdn01}${item.image}`,
        type: item.mimeType?.startsWith('video') ? 'video' : 'image',
      })),
    [media],
  );

  const onClose = (ev: MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
    setGalleryOpen(false);
  };

  const onMediaClick = (
    ev: MouseEvent<HTMLImageElement | HTMLVideoElement>,
    index: number,
  ) => {
    ev.stopPropagation();
    setStartIndex(index);
    setGalleryOpen(true);
  };

  const onWrapperClick = (ev: MouseEvent<HTMLDivElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
  };

  return (
    <Box>
      <Modal
        onClose={onClose}
        open={isGalleryOpen}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: '#151E34',
              opacity: '0.9 !important',
            },
          },
        }}
      >
        <Box
          className="flex m-auto h-[100%] w-[100%] md:w-[842px]"
          onClick={onWrapperClick}
        >
          <Box className="flex flex-col m-auto h-[636px] items-center w-[90%] md:w-[100%]">
            <Box className="flex justify-end md:justify:start w-[100%]">
              <Button variant="text" onClick={onClose}>
                <FontAwesomeIcon icon={faXmark} className="text-white" />
              </Button>
            </Box>
            <Box
              sx={{
                display: 'flex',
                margin: 'auto',
                height: 636,
                alignItems: 'center',
                '& .image-gallery > .image-gallery-content > .image-gallery-slide-wrapper':
                  {
                    display: 'flex',
                  },
              }}
            >
              <ImageGallery
                items={mappedMedia}
                infinite={false}
                showIndex={false}
                showPlayButton={false}
                startIndex={startIndex}
                showFullscreenButton={false}
                showThumbnails={!isMobileView}
                showNav={!isMobileView}
                renderLeftNav={GalleryNavigationLeft}
                renderRightNav={GalleryNavigationRight}
                renderItem={(item) =>
                  RenderGalleryItem(item as ReactImageGalleryItemCustom)
                }
                renderThumbInner={(item) =>
                  RenderGalleryThumbnail(item as ReactImageGalleryItemCustom)
                }
              />
            </Box>
          </Box>
        </Box>
      </Modal>
      {!_.isEmpty(media) && (
        <Box
          sx={{
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
          className={classNames('flex gap-2.5', {
            'justify-start overflow-x-scroll': media.length > 1,
            'justify-center': media.length === 1,
          })}
        >
          {media.map((item, index) => (
            <Fragment key={item.image || item.video}>
              {item.image && !item.video && (
                <img
                  src={`${item.cdn01}${item.image}`}
                  className={classNames('rounded-[10px] object-cover', {
                    'max-h-[486px]': media.length === 1 && !isPostPage,
                    'max-h-[180px]': media.length > 1 || isPostPage,
                  })}
                  loading="lazy"
                  onClick={(ev) => onMediaClick(ev, index)}
                />
              )}
              {item.video && (
                <div className="h-[180px] min-w-[118px] relative scroll">
                  <div className="absolute bottom-0 left-0 right-0 top-0 grid place-items-center">
                    <FontAwesomeIcon
                      icon={faPlay as IconProp}
                      className="text-white"
                    />
                  </div>
                  <video
                    src={`${item.cdn01}${item.video}`}
                    className="h-[100%] w-[100%] rounded-[10px]"
                    onClick={(ev) => onMediaClick(ev, index)}
                  />
                </div>
              )}
            </Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};
